import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  listWorkflows,
  createWorkflow,
  deleteWorkflow,
  runWorkflow,
  type Workflow,
} from "../api/client.js";

export function WorkflowListPage() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    loadWorkflows();
  }, []);

  async function loadWorkflows() {
    try {
      const res = await listWorkflows();
      setWorkflows(res.workflows);
    } catch {
      // handled silently
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteWorkflow(id);
      setWorkflows((prev) => prev.filter((w) => w.id !== id));
    } catch {
      // handled silently
    }
  }

  async function handleRun(workflow: Workflow) {
    try {
      const run = await runWorkflow(workflow.id);
      navigate(`/workflows/${workflow.id}/runs/${run.runId}`);
    } catch {
      // handled silently
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-ink-100 bg-white px-6">
        <h1 className="text-sm font-semibold text-ink-900">Workflows</h1>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs">
          <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
          New Workflow
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-6">
          {loading ? (
            <LoadingSkeleton />
          ) : workflows.length === 0 ? (
            <EmptyState onAction={() => setShowCreate(true)} />
          ) : (
            <div className="grid gap-3">
              {workflows.map((wf) => (
                <WorkflowCard
                  key={wf.id}
                  workflow={wf}
                  onDelete={() => handleDelete(wf.id)}
                  onRun={() => handleRun(wf)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateWorkflowModal
          onClose={() => setShowCreate(false)}
          onCreate={async (data) => {
            const wf = await createWorkflow(data);
            setWorkflows((prev) => [wf, ...prev]);
            setShowCreate(false);
            navigate(`/workflows/${wf.id}/edit`);
          }}
        />
      )}
    </div>
  );
}

function WorkflowCard({
  workflow,
  onDelete,
  onRun,
}: {
  workflow: Workflow;
  onDelete: () => void;
  onRun: () => void;
}) {
  const nodeCount = workflow.definition.nodes.length;
  const edgeCount = workflow.definition.edges.length;

  return (
    <div className="card flex items-center gap-4 px-5 py-4">
      {/* DAG icon */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-2">
        <svg className="h-5 w-5 text-ink-500" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="4" cy="4" r="2" />
          <circle cx="12" cy="4" r="2" />
          <circle cx="8" cy="12" r="2" />
          <path d="M5 5.5L7 10.5M11 5.5L9 10.5" />
        </svg>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-900">{workflow.name}</span>
          <span className={workflow.enabled ? "badge-green" : "badge-gray"}>
            {workflow.enabled ? "Active" : "Disabled"}
          </span>
        </div>
        {workflow.description && (
          <p className="mt-0.5 truncate text-xs text-ink-500">{workflow.description}</p>
        )}
        <div className="mt-1.5 flex items-center gap-3 text-2xs text-ink-400">
          <span>{nodeCount} node{nodeCount !== 1 ? "s" : ""}</span>
          <span>{edgeCount} edge{edgeCount !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={onRun}
          className="btn-ghost text-xs text-emerald-600 hover:bg-emerald-50"
          title="Run workflow"
        >
          <PlayIcon className="h-3.5 w-3.5" />
        </button>
        <Link
          to={`/workflows/${workflow.id}/edit`}
          className="btn-ghost text-xs"
          title="Edit"
        >
          <EditIcon className="h-3.5 w-3.5" />
        </Link>
        <button
          onClick={onDelete}
          className="btn-ghost text-xs text-danger hover:bg-red-50"
          title="Delete"
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function CreateWorkflowModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (data: { name: string; description?: string; definition: { nodes: never[]; edges: never[] }; agentId: string }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
        definition: { nodes: [], edges: [] },
        agentId: "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create workflow");
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-ink-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-4 w-full max-w-md animate-slide-up rounded-2xl bg-white p-6 shadow-elevated">
        <h2 className="text-base font-semibold text-ink-900">New Workflow</h2>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="e.g. Data Processing Pipeline"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              placeholder="Optional description"
            />
          </div>

          {error && <p className="text-xs text-danger">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary text-xs">
              Cancel
            </button>
            <button type="submit" disabled={submitting || !name.trim()} className="btn-primary text-xs">
              {submitting ? "Creating..." : "Create & Edit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmptyState({ onAction }: { onAction: () => void }) {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2">
          <svg className="h-6 w-6 text-ink-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="4" cy="4" r="2" />
            <circle cx="12" cy="4" r="2" />
            <circle cx="8" cy="12" r="2" />
            <path d="M5 5.5L7 10.5M11 5.5L9 10.5" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-ink-800">No workflows yet</h2>
        <p className="mt-1 text-xs text-ink-500">Create a workflow to orchestrate agent tasks</p>
        <button onClick={onAction} className="btn-primary mt-4 text-xs">
          Create Workflow
        </button>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="card animate-pulse px-5 py-4">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-surface-2" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-36 rounded bg-surface-2" />
              <div className="h-2.5 w-52 rounded bg-surface-2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M8 3v10M3 8h10" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M4 2.5v11l9-5.5L4 2.5z" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M2 4h12M5.5 4V2.5h5V4M6 7v5M10 7v5M3.5 4l.5 9.5a1 1 0 0 0 1 .5h6a1 1 0 0 0 1-.5L12.5 4" />
    </svg>
  );
}
